import {
    Add as AddIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Mic as MicIcon,
    MicNone as MicNoneIcon,
    Pause as PauseIcon,
    PlayArrow as PlayIcon,
    Stop as StopIcon,
} from '@mui/icons-material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { useContext, useRef, useState } from 'react';
import { AudioVisualizer } from 'react-audio-visualize';
import Dropzone from 'react-dropzone';
import { OnlyFansSubscriber } from '../../../hooks/useSubscriber';
import useTtsReferenceModels, { TtsReferenceModel } from '../../../hooks/useTtsReferenceModels';
import { UserContext } from '../../../store/UserContext';
import DialogAdaptive from '../../common/DialogAdaptive';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';
import StyledDialogActions from '../../common/StyledDialogActions';

const VoiceModelManager = ({ open, onClose, subscriber }: { open: boolean; onClose: () => void; subscriber: OnlyFansSubscriber }) => {
    const theme: Theme = useTheme();
    const userContext = useContext(UserContext);
    const dialog = useDialog();

    const {
        data: models,
        isLoading: loading,
        refetch: loadModels,
        ttsGenerateTest,
        ttsGenerateTestLoading,
        ttsTrainModel,
        ttsTrainModelLoading,
        deleteModel,
        ttsDeleteModelLoading,
    } = useTtsReferenceModels();

    const [error, setError] = useState<string | null>(null);
    const [selectedModel, setSelectedModel] = useState<TtsReferenceModel | null>(null);
    const [testPrompt, setTestPrompt] = useState('');
    const [testAudioURL, setTestAudioURL] = useState<string | null>(null);

    // Training form state
    const [showTrainingForm, setShowTrainingForm] = useState(false);
    const [modelName, setModelName] = useState('');
    const [modelDescription, setModelDescription] = useState('');

    // Recording state
    const [recordingState, setRecordingState] = useState<{
        isRecording: boolean;
        duration: number;
        mediaRecorder: MediaRecorder | null;
        audioChunks: Blob[];
    }>({
        isRecording: false,
        duration: 0,
        mediaRecorder: null,
        audioChunks: [],
    });

    const [audioState, setAudioState] = useState<{
        isRecording: boolean;
        isPlaying: boolean;
        currentTime: number;
        duration: number;
        audioURL: string | null;
        audioFile: File | null;
    }>({
        isRecording: false,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        audioURL: null,
        audioFile: null,
    });

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const recordingInterval = useRef<NodeJS.Timeout | null>(null);
    const visualizerRef = useRef<HTMLCanvasElement>(null);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks: Blob[] = [];
            let currentDuration = 0;

            mediaRecorder.ondataavailable = event => {
                if (event.data.size > 0) {
                    audioChunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                const audioURL = URL.createObjectURL(audioBlob);
                const audioFile: File = new File([audioBlob], `recording_${new Date().toISOString()}.webm`, {
                    type: 'audio/webm',
                    lastModified: Date.now(),
                });

                // Use the captured duration from our timer
                setAudioState({
                    isRecording: false,
                    isPlaying: false,
                    duration: currentDuration,
                    currentTime: 0,
                    audioURL,
                    audioFile,
                });

                // Optional: Try to get precise duration from metadata, but don't rely on it
                const audio = new Audio(audioURL);
                audio.onloadedmetadata = () => {
                    // Only update if we got a valid duration that's not infinity
                    if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                        setAudioState(prev => ({
                            ...prev,
                            duration: audio.duration,
                        }));
                    }
                };
            };

            mediaRecorder.start();
            setRecordingState({
                isRecording: true,
                duration: 0,
                mediaRecorder,
                audioChunks,
            });

            recordingInterval.current = setInterval(() => {
                currentDuration += 1;
                setRecordingState(prev => ({
                    ...prev,
                    duration: prev.duration + 1,
                }));
            }, 1000);
        } catch (err) {
            setError('Failed to access microphone');
        }
    };

    const stopRecording = () => {
        if (recordingState.mediaRecorder) {
            recordingState.mediaRecorder.stop();
            recordingState.mediaRecorder.stream.getTracks().forEach(track => track.stop());
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
            }
            setRecordingState(prev => ({
                ...prev,
                isRecording: false,
            }));
        }
    };

    const togglePlayback = () => {
        if (audioRef.current) {
            if (audioState.isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
                setAudioState(prev => ({
                    ...prev,
                    currentTime: 0,
                }));
                audioRef.current.currentTime = 0;
                audioRef.current.ontimeupdate = () => {
                    setAudioState(prev => ({
                        ...prev,
                        currentTime: audioRef.current?.currentTime || 0,
                    }));
                };
                audioRef.current.onended = () => {
                    setAudioState(prev => ({
                        ...prev,
                        isPlaying: false,
                    }));
                };
            }
            setAudioState(prev => ({
                ...prev,
                isPlaying: !prev.isPlaying,
            }));
        }
    };

    const handleFileUpload = (file: File) => {
        // Allow only WAV, MP3, M4A, and OGG files
        if (
            file &&
            (file.type === 'audio/wav' ||
                file.type === 'audio/mpeg' ||
                file.type === 'audio/mp3' ||
                file.type === 'audio/m4a' ||
                file.type === 'audio/x-m4a' ||
                file.type === 'audio/ogg')
        ) {
            const audioURL = URL.createObjectURL(file);
            const audio = new Audio(audioURL);

            audio.onloadedmetadata = () => {
                setAudioState({
                    isRecording: false,
                    isPlaying: false,
                    duration: audio.duration,
                    currentTime: 0,
                    audioURL,
                    audioFile: file,
                });
            };
        } else {
            setError('Please upload a WAV, MP3, M4A, or OGG file');
        }
    };

    const clearAudio = () => {
        if (audioState.audioURL) {
            URL.revokeObjectURL(audioState.audioURL);
        }
        setAudioState({
            isRecording: false,
            isPlaying: false,
            duration: 0,
            currentTime: 0,
            audioURL: null,
            audioFile: null,
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleTrainModel = async () => {
        if (!audioState.audioFile || !modelName) return;

        setError(null);

        try {
            await ttsTrainModel(modelName, modelDescription, [audioState.audioFile]).then(newMode => {
                if (newMode) {
                    setShowTrainingForm(false);
                    loadModels();
                    clearAudio();
                    setModelName('');
                    setModelDescription('');
                }
            });
        } catch (err) {
            setError('Failed to train model');
        }
    };

    const handleTestPrompt = async () => {
        if (!selectedModel || !testPrompt) return;

        try {
            const response = await ttsGenerateTest(selectedModel.modelId, testPrompt, selectedModel.provider);

            if (response && response.success && response.url) {
                // In a real implementation, you'd fetch the audio from S3
                // This is just a placeholder
                setTestAudioURL(response.url);
            } else {
                setTestAudioURL(null);
            }
        } catch (err) {
            setError('Failed to generate test audio');
            setTestAudioURL(null);
        }
    };

    const handleDeleteModel = async (modelId: string) => {
        dialog
            .confirm({
                title: 'Delete Model',
                message: 'Are you sure you want to delete this model?',
                ok: {
                    variant: 'contained',
                    color: 'error',
                    text: 'Delete',
                },
                cancel: {
                    variant: 'outlined',
                    color: 'primary',
                    text: 'Cancel',
                },
            })
            .then(() => {
                deleteModel(modelId)
                    .then(() => {
                        loadModels();
                        setSelectedModel(null);
                    })
                    .catch(err => {
                        setError('Failed to delete model');
                        console.error('Error deleting model:', err);
                    });
            })
            .catch(() => {
                // User cancelled
            });
    };

    const isAudioValid = audioState.duration >= 20 && audioState.duration <= 90;
    const canTrain = isAudioValid && modelName.trim() !== '';

    return (
        <DialogAdaptive open={open} onClose={onClose} maxWidth="md" fullWidth>
            <StyledCardTitleBar title="Voice Model Manager" isLoading={loading || ttsGenerateTestLoading} />

            <DialogContent>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {showTrainingForm ? (
                    <Stack direction="column" spacing={2}>
                        <Typography variant="h6">Train New Model</Typography>

                        <Typography variant="body2" color="text.secondary">
                            Record or upload audio to train your voice model. The audio should be between 20 and 60 seconds long, clear, and
                            without background noise. Make sure speak in the same tone and style you want the model to learn.
                        </Typography>

                        <Box>
                            {!audioState.audioURL ? (
                                <Box textAlign="center">
                                    {recordingState.isRecording ? (
                                        <Box
                                            sx={{
                                                border: '2px dashed',
                                                borderColor: 'error.main',
                                            }}
                                        >
                                            <IconButton size="large" color="error" onClick={stopRecording} sx={{ mb: 2 }}>
                                                <StopIcon fontSize="large" />
                                            </IconButton>
                                            <Typography variant="body1">Recording: {recordingState.duration}s</Typography>
                                        </Box>
                                    ) : (
                                        <Dropzone
                                            onDrop={acceptedFiles => handleFileUpload(acceptedFiles[0])}
                                            accept={{
                                                'audio/wav': ['.wav'],
                                                'audio/mpeg': ['.mp3'],
                                                'audio/mp3': ['.mp3'],
                                                'audio/m4a': ['.m4a'],
                                                'audio/x-m4a': ['.m4a'],
                                                'audio/ogg': ['.ogg'],
                                            }}
                                            noClick
                                            noKeyboard
                                        >
                                            {({ getRootProps, getInputProps, isDragActive }) => (
                                                <Box
                                                    {...getRootProps()}
                                                    sx={{
                                                        border: '1px dashed',
                                                        borderColor: isDragActive ? 'primary.main' : 'text.secondary',
                                                        backgroundColor: isDragActive ? 'grey.50' : 'background.paper',
                                                        borderRadius: 2,
                                                        p: 2,
                                                        '&:hover': {
                                                            borderColor: 'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <Stack direction="row" alignItems="center" justifyContent="center">
                                                        <Stack direction="column" alignItems="center" justifyContent="center" flexGrow={1}>
                                                            <IconButton
                                                                size="large"
                                                                color="primary"
                                                                onClick={startRecording}
                                                                sx={{ mb: 1 }}
                                                            >
                                                                <MicIcon fontSize="large" />
                                                            </IconButton>
                                                            <Typography variant="body1">Click to Record</Typography>
                                                        </Stack>
                                                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                                                        <Stack direction="column" alignItems="center" justifyContent="center" flexGrow={1}>
                                                            <input
                                                                type="file"
                                                                ref={fileInputRef}
                                                                accept="audio/wav,audio/mp3"
                                                                onChange={event => {
                                                                    if (event.target.files && event.target.files.length > 0) {
                                                                        handleFileUpload(event.target.files[0]);
                                                                    }
                                                                }}
                                                                style={{ display: 'none' }}
                                                            />
                                                            <IconButton
                                                                size="large"
                                                                color="primary"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                sx={{ mb: 1 }}
                                                            >
                                                                <FileUploadIcon fontSize="large" />
                                                            </IconButton>
                                                            <Typography variant="body1">Upload Audio File</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Dropzone>
                                    )}
                                </Box>
                            ) : (
                                <Paper variant="outlined" sx={{ pt: 1, pb: 1, pl: 2, pr: 2, textAlign: 'center' }}>
                                    {audioState.audioFile && (
                                        <AudioVisualizer
                                            ref={visualizerRef}
                                            blob={audioState.audioFile}
                                            width={500}
                                            height={75}
                                            barWidth={1}
                                            gap={0}
                                            barColor={theme.palette.secondary.light}
                                            barPlayedColor={theme.palette.primary.main}
                                            currentTime={audioState.currentTime}
                                            style={{ width: '100%', height: '75px' }}
                                        />
                                    )}

                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Tooltip title="Play/Pause">
                                                <IconButton onClick={togglePlayback} color="primary">
                                                    {audioState.isPlaying ? <PauseIcon /> : <PlayIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Clear Audio">
                                                <IconButton
                                                    onClick={() => {
                                                        dialog
                                                            .confirm({
                                                                title: 'Discard Audio',
                                                                message: 'Are you sure you want to discard the audio?',
                                                                ok: {
                                                                    variant: 'contained',
                                                                    color: 'error',
                                                                    text: 'Delete',
                                                                },
                                                                cancel: {
                                                                    variant: 'outlined',
                                                                    color: 'primary',
                                                                    text: 'Cancel',
                                                                },
                                                            })
                                                            .then(() => {
                                                                clearAudio();
                                                            })
                                                            .catch(() => {
                                                                // User cancelled
                                                            });
                                                    }}
                                                    color="error"
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography variant="body2" color={isAudioValid ? 'success.main' : 'error.main'}>
                                                {isAudioValid ? (
                                                    <Box component="span" display="flex" alignItems="center">
                                                        <CheckIcon fontSize="small" />
                                                    </Box>
                                                ) : audioState.duration < 20 ? (
                                                    `Too short`
                                                ) : (
                                                    `Too long`
                                                )}
                                            </Typography>
                                            <Typography variant="body2">{audioState.duration.toFixed(1)}s</Typography>
                                        </Stack>
                                    </Stack>

                                    <audio
                                        ref={audioRef}
                                        src={audioState.audioURL}
                                        onEnded={() => setAudioState(prev => ({ ...prev, isPlaying: false }))}
                                        style={{ display: 'none' }}
                                    />
                                </Paper>
                            )}
                        </Box>

                        <TextField
                            fullWidth
                            label="Model Name"
                            value={modelName}
                            onChange={e => setModelName(e.target.value)}
                            margin="normal"
                            required
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            value={modelDescription}
                            onChange={e => setModelDescription(e.target.value)}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                    </Stack>
                ) : (
                    <Stack direction="column" spacing={2}>
                        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Your Voice Models</Typography>
                            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setShowTrainingForm(true)}>
                                Train New Model
                            </Button>
                        </Stack>

                        {models && models.length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <MicNoneIcon fontSize="large" color="disabled" />
                                <Typography variant="body1" color="text.secondary" mt={2}>
                                    No voice models yet. Train your first model!
                                </Typography>
                            </Paper>
                        ) : (
                            <div>
                                <Grid container spacing={1}>
                                    {models?.map(model => (
                                        <Grid item xs={12} key={model._id}>
                                            <Card
                                                onClick={() => setSelectedModel(model)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    border: 2,
                                                    borderColor: selectedModel?._id === model._id ? 'primary.main' : 'divider',
                                                    borderRadius: theme.shape.borderRadius,
                                                    '&:hover': {
                                                        border: 2,
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            >
                                                <CardContent>
                                                    <Stack direction="row" flexGrow={1} alignItems="center" justifyContent="space-between">
                                                        <Stack direction="column" flexGrow={1}>
                                                            <Typography variant="body1" component="div">
                                                                {model.name}
                                                            </Typography>
                                                            {model.description && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {model.description}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Tooltip title="Delete Model">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={ttsDeleteModelLoading}
                                                                    onClick={e => {
                                                                        e.stopPropagation();

                                                                        handleDeleteModel(model.modelId);
                                                                    }}
                                                                >
                                                                    <DeleteForeverIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        )}

                        {selectedModel && (
                            <Stack direction="column" spacing={2}>
                                <Typography variant="h6">Test Selected Model</Typography>

                                <Typography variant="body1" color="text.secondary" pb={1}>
                                    The prompt should be between 20 and 300 characters long.{' '}
                                    <strong>
                                        Make sure to use proper punctuation. Add commas for natural pauses. It really helps the flow of the
                                        generated voice.
                                    </strong>{' '}
                                    The generated audio will be played back for you to evaluate the model's performance.
                                </Typography>

                                <TextField
                                    fullWidth
                                    label="Enter text to test"
                                    value={testPrompt}
                                    onChange={e => {
                                        if (e.target.value.length <= 300) {
                                            setTestPrompt(e.target.value);
                                        }
                                    }}
                                    multiline
                                    rows={4}
                                    margin="normal"
                                    placeholder="Hello, this is a test of my voice model..."
                                    inputProps={{ maxLength: 300 }}
                                    helperText={`${testPrompt.length} / 300 characters`}
                                    error={testPrompt.length > 300}
                                />

                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    loading={loading || ttsGenerateTestLoading}
                                    disabled={!testPrompt || testPrompt.length > 300}
                                    onClick={handleTestPrompt}
                                    sx={{ mt: 2 }}
                                >
                                    <span>Generate Test Audio</span>
                                </LoadingButton>

                                {testAudioURL && (
                                    <div>
                                        <Typography variant="body2" gutterBottom>
                                            Preview:
                                        </Typography>
                                        <audio
                                            controls
                                            src={testAudioURL}
                                            // no download attribute to prevent download
                                            controlsList="nodownload"
                                            style={{ width: '100%' }}
                                            onContextMenu={e => {
                                                if (!userContext.isAdmin) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <StyledDialogActions>
                {showTrainingForm ? (
                    <>
                        <Button onClick={() => setShowTrainingForm(false)}>Cancel</Button>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            disabled={!canTrain}
                            loading={ttsTrainModelLoading}
                            onClick={handleTrainModel}
                        >
                            <span>{ttsTrainModelLoading ? 'Training...' : 'Train Model'}</span>
                        </LoadingButton>
                    </>
                ) : (
                    <Button onClick={onClose}>Close</Button>
                )}
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default VoiceModelManager;
